import { useState } from 'react';
import { DeleteOutlined, ExportOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Divider,
  Drawer,
  Empty,
  Flex,
  Form,
  Input,
  Select,
  Skeleton,
  Space,
  Typography,
  theme,
} from 'antd';
import dayjs from 'dayjs';
import type {
  MagazineArticleAdmin as ArtigoDoAdmin,
  MagazineIssueAdmin as EdicaoDoAdmin,
} from '@titularis/api-client';
import { api } from '../api';
import { enderecoDoArtigo } from '../site';
import {
  SECAO_LABEL,
  entradaDoArtigo,
  situacaoDoArtigo,
  type ValoresDoArtigo,
} from '../pages/revista/artigo';
import { Numero, GraficoDeUso } from './UsoDoArquivo';
import { EnvioDeArquivo } from './EnvioDeArquivo';

/**
 * O artigo ocupa uma gaveta porque autoria e bibliografia são listas longas:
 * um Modal como o da edição faria a redação disputar espaço com o formulário.
 * As duas abas mantêm o conjunto e seus trabalhos na mesma mesa, mas cada um
 * conserva a própria data. Não há botão publicar: a data é a publicação nos
 * dois níveis, e uma segunda chave poderia contradizer o que ela diz.
 *
 * "Aguarda edição" significa que a data do artigo já venceu, mas a da edição
 * ainda não. O trabalho está pronto sem estar público. Por isso o endereço e
 * os números abaixo seguem o cadastro salvo, nunca a data ainda sendo digitada:
 * editar um campo não pode anunciar que o artigo já está no ar.
 */
export function FormularioDoArtigo({
  artigoId,
  edicoes,
  onFechar,
}: {
  artigoId: string | null;
  edicoes: EdicaoDoAdmin[];
  onFechar: () => void;
}) {
  const artigo = useQuery({
    queryKey: ['revista', 'artigo', artigoId],
    queryFn: () => api.getMagazineArticleById(artigoId!),
    enabled: Boolean(artigoId),
  });

  return (
    <Drawer
      open
      title={artigoId ? 'Editar artigo' : 'Novo artigo'}
      size="min(900px, 100vw)"
      onClose={onFechar}
      destroyOnHidden
    >
      {artigoId && artigo.isPending ? (
        <Skeleton active />
      ) : artigoId && artigo.error ? (
        <Alert
          type="error"
          showIcon
          title={artigo.error.message}
          action={
            <Button onClick={() => void artigo.refetch()}>Tentar novamente</Button>
          }
        />
      ) : (
        <CamposDoArtigo
          artigo={artigo.data ?? null}
          edicoes={edicoes}
          onFechar={onFechar}
        />
      )}
    </Drawer>
  );
}

function CamposDoArtigo({
  artigo,
  edicoes,
  onFechar,
}: {
  artigo: ArtigoDoAdmin | null;
  edicoes: EdicaoDoAdmin[];
  onFechar: () => void;
}) {
  const { token } = theme.useToken();
  const { message: mensagem } = App.useApp();
  const consultas = useQueryClient();
  const [formulario] = Form.useForm<ValoresDoArtigo>();
  const [salvo, setSalvo] = useState(artigo);
  const edicao = edicoes.find((item) => item.id === salvo?.issueId);
  const publicado = Boolean(
    salvo && edicao && situacaoDoArtigo(salvo.publishedAt, edicao.publishedAt) === 'no_ar',
  );

  const receberSalvo = async (dados: ArtigoDoAdmin) => {
    setSalvo(dados);
    consultas.setQueryData(['revista', 'artigo', dados.id], dados);
    await consultas.invalidateQueries({ queryKey: ['revista'] });
  };
  const salvar = useMutation({
    mutationFn: (valores: ValoresDoArtigo) => salvo
      ? api.updateMagazineArticle(salvo.id, entradaDoArtigo(valores))
      : api.createMagazineArticle(entradaDoArtigo(valores)),
    onSuccess: async (dados) => {
      await receberSalvo(dados);
      mensagem.success('Artigo salvo.');
    },
    onError: (erro: Error) => mensagem.error(erro.message),
  });
  const enviarCapa = useMutation({
    mutationFn: (arquivo: File) => api.uploadMagazineArticleCover(salvo!.id, arquivo),
    onSuccess: async (dados) => {
      await receberSalvo(dados);
      mensagem.success('Capa enviada.');
    },
    onError: (erro: Error) => mensagem.error(erro.message),
  });
  const enviarPdf = useMutation({
    mutationFn: (arquivo: File) => api.uploadMagazineArticlePdf(salvo!.id, arquivo),
    onSuccess: async (dados) => {
      await receberSalvo(dados);
      mensagem.success('PDF enviado.');
    },
    onError: (erro: Error) => mensagem.error(erro.message),
  });
  // As três escritas devolvem o cadastro inteiro. Serializar evita uma resposta
  // antiga de upload esconder a capa ou a data que acabou de ser salva.
  const ocupado = salvar.isPending || enviarCapa.isPending || enviarPdf.isPending;
  const endereco = salvo ? enderecoDoArtigo(salvo.slug) : null;

  return (
    <>
      <Form
        form={formulario}
        layout="vertical"
        onFinish={(valores) => salvar.mutate(valores)}
        initialValues={{
          ...artigo,
          issueId: artigo?.issueId,
          section: artigo?.section ?? 'article',
          authors: artigo?.authors ?? [{
            name: '',
            affiliation: '',
            bio: '',
          }],
          keywords: artigo?.keywords ?? [],
          references: artigo?.references.join('\n') ?? '',
          license: artigo?.license ?? 'cc-by-nc-nd',
          publishedAt: artigo?.publishedAt ? dayjs(artigo.publishedAt) : null,
        }}
      >
        <Form.Item
          name="issueId"
          label="Edição"
          rules={[{
            required: true,
            message: 'Escolha a edição do artigo.',
          }]}
        >
          <Select
            showSearch={{ optionFilterProp: 'label' }}
            options={edicoes.map((edicao) => ({
              value: edicao.id,
              label: edicao.label,
            }))}
            placeholder="Escolha uma edição"
          />
        </Form.Item>
        <Form.Item
          name="section"
          label="Seção"
          rules={[{ required: true }]}
        >
          <Select options={Object.entries(SECAO_LABEL).map(([valor, rotulo]) => ({
            value: valor,
            label: rotulo,
          }))} />
        </Form.Item>
        <Form.Item
          name="title"
          label="Título"
          rules={[{
            required: true,
            whitespace: true,
            message: 'O artigo precisa de um título.',
          }]}
          extra="O endereço público muda se o título mudar."
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="subtitle" label="Subtítulo">
          <Input maxLength={500} />
        </Form.Item>

        <Form.List
          name="authors"
          rules={[{
            validator: async (_, autores: ValoresDoArtigo['authors']) => {
              if (!autores?.length) throw new Error('Acrescente pelo menos um autor.');
            },
          }]}
        >
          {(campos, {
            add: acrescentar,
            remove: remover,
          }, { errors: erros }) => (
            <Space
              orientation="vertical"
              size={token.marginSM}
              style={{
                width: '100%',
                marginBottom: token.marginLG,
              }}
            >
              <Typography.Text strong>Autores</Typography.Text>
              {campos.map(({
                key: chave,
                name: indice,
                ...resto
              }) => (
                <Card
                  key={chave}
                  size="small"
                  title={`Autor ${indice + 1}`}
                  extra={
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={`Remover autor ${indice + 1}`}
                      disabled={campos.length === 1}
                      onClick={() => remover(indice)}
                    />
                  }
                >
                  <Form.Item
                    {...resto}
                    name={[indice, 'name']}
                    label="Nome"
                    rules={[{
                      required: true,
                      whitespace: true,
                      message: 'Informe o nome do autor.',
                    }]}
                  >
                    <Input maxLength={200} />
                  </Form.Item>
                  <Form.Item
                    {...resto}
                    name={[indice, 'affiliation']}
                    label="Vínculo"
                  >
                    <Input maxLength={500} />
                  </Form.Item>
                  <Form.Item
                    {...resto}
                    name={[indice, 'bio']}
                    label="Biografia"
                  >
                    <Input.TextArea rows={3} maxLength={10000} />
                  </Form.Item>
                </Card>
              ))}
              <Form.ErrorList errors={erros} />
              <Button
                icon={<PlusOutlined />}
                disabled={campos.length >= 50}
                onClick={() => acrescentar({
                  name: '',
                  affiliation: '',
                  bio: '',
                })}
              >
                Acrescentar autor
              </Button>
            </Space>
          )}
        </Form.List>

        <Form.Item
          name="abstract"
          label="Resumo"
          rules={[{
            required: true,
            whitespace: true,
            message: 'O artigo precisa de um resumo.',
          }]}
        >
          <Input.TextArea rows={6} maxLength={50000} />
        </Form.Item>
        <Form.Item name="keywords" label="Palavras-chave">
          <Select
            mode="tags"
            tokenSeparators={[';']}
            placeholder="Digite e pressione Enter"
          />
        </Form.Item>
        <Form.Item
          name="references"
          label="Referências"
          extra="Cole uma referência por linha."
        >
          <Input.TextArea rows={6} />
        </Form.Item>
        <Form.Item
          name="license"
          label="Licença"
          rules={[{ required: true }]}
        >
          <Select options={[
            {
              value: 'cc-by',
              label: 'CC BY 4.0',
            },
            {
              value: 'cc-by-nc',
              label: 'CC BY-NC 4.0',
            },
            {
              value: 'cc-by-nc-nd',
              label: 'CC BY-NC-ND 4.0',
            },
            {
              value: 'cc-by-sa',
              label: 'CC BY-SA 4.0',
            },
          ]} />
        </Form.Item>
        <Form.Item
          name="publishedAt"
          label="Publicação"
          extra={
            'Vazio deixa em rascunho. Data futura agenda. Data passada põe no ar, ' +
            'desde que a edição também esteja publicada.'
          }
        >
          <DatePicker
            showTime
            format="DD/MM/YYYY HH:mm"
            style={{ width: '100%' }}
          />
        </Form.Item>
        <EnvioDeArquivo
          tipo="capa"
          salvo={Boolean(salvo)}
          temArquivo={salvo?.temCapa ?? false}
          enviando={enviarCapa.isPending}
          desabilitado={ocupado}
          onEnviar={(arquivo) => enviarCapa.mutate(arquivo)}
        />
        <EnvioDeArquivo
          tipo="pdf"
          salvo={Boolean(salvo)}
          temArquivo={salvo?.temPdf ?? false}
          enviando={enviarPdf.isPending}
          desabilitado={ocupado}
          onEnviar={(arquivo) => enviarPdf.mutate(arquivo)}
        />

        <Flex gap={token.marginSM} justify="flex-end">
          <Button onClick={onFechar} disabled={ocupado}>Fechar</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={salvar.isPending}
            disabled={ocupado}
          >
            Salvar
          </Button>
        </Flex>
      </Form>

      {salvo && endereco && (
        <>
          <Divider />
          <Typography.Text type="secondary">
            {publicado
              ? 'Endereço público do artigo:'
              : 'Endereço público, disponível quando o artigo e a edição estiverem no ar:'}
          </Typography.Text>
          <Typography.Paragraph
            copyable={{ text: endereco }}
            style={{ overflowWrap: 'anywhere' }}
          >
            {publicado ? (
              <a
                href={endereco}
                target="_blank"
                rel="noopener noreferrer"
              >
                {endereco} <ExportOutlined />
              </a>
            ) : endereco}
          </Typography.Paragraph>
          {publicado && <UsoDoArtigo artigoId={salvo.id} />}
        </>
      )}
    </>
  );
}

function UsoDoArtigo({ artigoId }: { artigoId: string }) {
  const { token } = theme.useToken();
  const engajamento = useQuery({
    queryKey: ['revista', 'engajamento', artigoId, 30],
    queryFn: () => api.magazineArticleEngagement(artigoId, 30),
    staleTime: 60_000,
  });
  if (engajamento.isPending) {
    return (
      <Skeleton
        active
        paragraph={{ rows: 2 }}
        title={false}
      />
    );
  }
  if (engajamento.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={engajamento.error.message}
        action={
          <Button onClick={() => void engajamento.refetch()}>Tentar novamente</Button>
        }
      />
    );
  }
  const dados = engajamento.data;
  return (
    <Space
      orientation="vertical"
      size={token.marginMD}
      style={{ width: '100%' }}
    >
      <Flex gap={token.marginLG} wrap>
        <Numero
          rotulo="Downloads"
          valor={dados.downloads}
          destaque
        />
        <Numero rotulo="Impressões pedidas" valor={dados.prints} />
        <Numero rotulo="Aberturas" valor={dados.views} />
      </Flex>
      <Typography.Text type="secondary">
        Totais acumulados. Impressão pedida conta o clique em Imprimir;
        o diálogo pode ter sido cancelado.
      </Typography.Text>
      <Typography.Text strong>Uso nos últimos 30 dias</Typography.Text>
      {dados.serie.length ? (
        <GraficoDeUso serie={dados.serie} />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nenhum uso nos últimos 30 dias."
        />
      )}
    </Space>
  );
}
